﻿import { FC, useContext, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useMatch, useNavigate } from "react-router";

import { StepGuard } from "@/components/Checkout/StepGuard/StepGuard";
import { Button } from "@/components/Interface/Button/Button";
import { StepMenuItem } from "@/components/Interface/Stepper/StepMenu";
import { StepFlowLayout } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowLayout";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import {
  ADD_DEVICE_TOTAL_STEPS,
  addDeviceStepMapping,
  addDeviceStepPaths,
} from "@/components/Portal/AddDevice/addDevice.constants";
import { AddDeviceBlockedModal } from "@/components/Portal/AddDevice/AddDeviceBlockedModal/AddDeviceBlockedModal";
import { AddDeviceOverflowMenu } from "@/components/Portal/AddDevice/AddDeviceOverflowMenu/AddDeviceOverflowMenu";
import { DataContext } from "@/provider/DataContextProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useGetSimCustomerActions, useInvalidateBasket } from "@/services/api";
import {
  getAddDeviceBlockedReasons,
  isAddDeviceFullyBlocked,
} from "@/utils/customerUtils";
import { getEntry } from "@/utils/dataContextHelpers";
import { useHasPrechargedMobileAbo } from "@/utils/tariffUtils";
import {
  getAddDeviceStepNumbering,
  getAddDeviceSteps,
  resolveWizardOptionId,
} from "@/utils/wizardNavigation";

type AddDeviceLocationState = {
  /** Set by flows that already prepared a basket (e.g. Flat Mobile upsell). */
  keepBasket?: boolean;
};

export const AddDevice: FC = () => {
  const { t } = useTranslation("portal", { keyPrefix: "add-device" });
  const { t: tCommon } = useTranslation();
  const { dataContext, setDataContext } = useContext(DataContext);
  const location = useLocation();
  const navigate = useNavigate();
  const routeMatch = useMatch(`${portalRoutes.addDevice.base}/:step/:substep`);
  // Capture once on mount — later in-wizard navigations drop location.state and
  // must not re-trigger a basket wipe (that bounces the user back to step 1).
  const keepBasketOnEntry = useRef(
    !!(location.state as AddDeviceLocationState | null)?.keepBasket,
  );
  const [isReady, setIsReady] = useState(keepBasketOnEntry.current);
  // Read inside the effect without adding `dataContext` to its deps - that
  // object changes on every keystroke elsewhere in the wizard and would
  // make this effect re-run far more often than the step changes it's
  // meant to track.
  const dataContextRef = useRef(dataContext);
  dataContextRef.current = dataContext;

  const { data: simCustomerInfo, isPending: isLoadingSimActions } =
    useGetSimCustomerActions();
  const { mutateAsync: invalidateBasket } = useInvalidateBasket();
  // Same reasoning as dataContextRef - read the latest mutate function inside
  // the effect without making its (react-query, not guaranteed stable)
  // identity part of the effect's deps.
  const invalidateBasketRef = useRef(invalidateBasket);
  invalidateBasketRef.current = invalidateBasket;
  const isBlocked = isAddDeviceFullyBlocked(simCustomerInfo);
  const blockedReasons = getAddDeviceBlockedReasons(simCustomerInfo);

  // Once the tariff is known (Number porting / Checkout), the stepper should
  // reflect only the steps this specific flow actually goes through - a
  // precharged, Flat Home, or purchase-intent tariff skips some of them.
  const hasPrechargedMobileAbo = useHasPrechargedMobileAbo(dataContext.basket);
  const entry = getEntry(dataContext);
  const optionId = resolveWizardOptionId({
    basketOptionId: entry?.options?.[0]?.product?.id,
    selectedOptionId: dataContext.selectedOption?.id,
    wizardEntryOptionId: dataContext.wizardEntry?.optionId,
  });
  const isPrecharged =
    !!dataContext.selectedOption?.precharged || hasPrechargedMobileAbo;

  // Reset step progress only when opening Add Device fresh from the cockpit.
  // Must not run on checkout refresh, Datatrans return, or any other remount
  // while the user is already mid-flow — that would drop allowedMax to 1 and
  // StepGuard would bounce them back to sim-selection.
  useLayoutEffect(() => {
    // Navigating away (Cancel, the blocked-modal close, ...) changes
    // `routeMatch` too - it stops matching once the URL moves outside
    // add-device entirely, which would otherwise read as "step 1" and
    // re-run the reset/cleanup logic below during the unmount transition.
    if (!location.pathname.startsWith(portalRoutes.addDevice.base)) {
      return;
    }

    if (keepBasketOnEntry.current) {
      setIsReady(true);

      return;
    }

    const currentStepNumber = routeMatch?.params.step
      ? Number.parseInt(routeMatch.params.step, 10)
      : 1;

    let cancelled = false;

    const resetForFreshEntry = async () => {
      // A purchase-intent basket is locked to that intent and never meant to
      // be resumed from a fresh entry - landing on step 1 without keepBasket
      // means it was abandoned uncleaned. Compax keeps returning it as "the
      // last unfinished basket" on POST /baskets until explicitly
      // invalidated, so clearing local state alone isn't enough.
      //
      // Scoped to eSIM: those intents always land on step 3/4, so step 1 is
      // unambiguously stale. Physical-SIM intents legitimately target step
      // 1, indistinguishable from a plain refresh, so that case falls
      // through to the reset below unchanged.
      const stale = dataContextRef.current;
      const stalePurchaseIntentBasketId =
        currentStepNumber <= 1 &&
        stale.wizardEntry?.source === "purchase-intent" &&
        stale.wizardEntry.lockedSimType === "esim"
          ? stale.basket?.id
          : undefined;

      if (stalePurchaseIntentBasketId !== undefined) {
        try {
          await invalidateBasketRef.current({
            basketId: stalePurchaseIntentBasketId,
          });
        } catch (error) {
          console.error(
            "Failed to invalidate stale purchase-intent basket:",
            error,
          );

          // Leave dataContext untouched on failure, same as handleCancel -
          // wizardEntry/basket stay in place so a subsequent Cancel click
          // gets another chance to invalidate this basket instead of the
          // reference being dropped here with no further retry possible.
          if (!cancelled) {
            setIsReady(true);
          }

          return;
        }

        if (cancelled) {
          return;
        }

        setDataContext((prev) => ({
          allowedMaxOnboardingStep: 1,
          isEmailVerified: prev.isEmailVerified ?? false,
        }));
        setIsReady(true);

        return;
      }

      setDataContext((prev) => {
        // Completed onboarding — undefined means "no step lock" for StepGuard.
        if (prev.allowedMaxOnboardingStep === undefined) {
          return prev;
        }

        const hasInProgressFlow =
          currentStepNumber > 1 ||
          (prev.basket?.id !== undefined &&
            (prev.allowedMaxOnboardingStep ?? 0) > 1);

        if (hasInProgressFlow) {
          return prev;
        }

        return {
          allowedMaxOnboardingStep: 1,
          isEmailVerified: prev.isEmailVerified ?? false,
        };
      });

      if (!cancelled) {
        setIsReady(true);
      }
    };

    void resetForFreshEntry();

    return () => {
      cancelled = true;
    };
  }, [routeMatch?.params.step, location.pathname, setDataContext]);

  const handleCancel = async () => {
    // A purchase-intent basket is locked to that specific intent - unlike a
    // normal in-progress flow, it's not meant to be resumable, so leaving it
    // in dataContext would make the next "Add subscription" entry pick it back
    // up as if it were current (see useActivatePurchaseIntent). Compax hands
    // back this same basket as "the last unfinished basket" on any new
    // POST /baskets until it's explicitly invalidated, so clearing local
    // state alone isn't enough.
    if (
      dataContext.wizardEntry?.source === "purchase-intent" &&
      dataContext.basket?.id
    ) {
      try {
        await invalidateBasket({ basketId: dataContext.basket.id });

        setDataContext((prev) => ({
          allowedMaxOnboardingStep: undefined,
          isEmailVerified: prev.isEmailVerified,
        }));
      } catch (error) {
        // Leave dataContext untouched on failure rather than clearing it
        // regardless - the mount-reset effect above retries this same
        // invalidation on the next fresh "Add subscription" entry, which only
        // triggers while `wizardEntry`/`basket` are still present to detect.
        console.error(
          "Failed to invalidate abandoned purchase-intent basket:",
          error,
        );
      }
    }

    navigate(portalRoutes.cockpit.base);
  };

  const handleBasketDeleted = () => {
    setDataContext((prev) => ({
      allowedMaxOnboardingStep: undefined,
      isEmailVerified: prev.isEmailVerified,
    }));
  };

  const handleCloseBlockedModal = () => {
    navigate(portalRoutes.cockpit.base, { replace: true });
  };

  if (!isReady || isLoadingSimActions) {
    return <LoadingSpinner />;
  }

  if (isBlocked) {
    return (
      <AddDeviceBlockedModal
        reasons={blockedReasons}
        onClose={handleCloseBlockedModal}
      />
    );
  }

  const substep = routeMatch?.params.substep ?? "sim-selection";
  const currentStep =
    addDeviceStepMapping[substep] ?? addDeviceStepMapping["sim-selection"];
  const { step, totalSteps } = getAddDeviceStepNumbering({
    substep,
    optionId,
    isPrecharged,
    wizardEntry: dataContext.wizardEntry,
    fallbackTotalSteps: ADD_DEVICE_TOTAL_STEPS,
  });

  const menuItems: StepMenuItem[] = getAddDeviceSteps({
    optionId,
    isPrecharged,
    wizardEntry: dataContext.wizardEntry,
  }).map((stepSubstep) => {
    const stepMeta = addDeviceStepMapping[stepSubstep];

    return {
      // The step keys are looked up dynamically, which a literal-typed `t`
      // cannot express - see the flow's constants file.
      label: t(stepMeta.label),
      path: addDeviceStepPaths[stepSubstep],
      disabled:
        dataContext.allowedMaxOnboardingStep !== undefined &&
        dataContext.allowedMaxOnboardingStep <= stepMeta.step,
    };
  });

  return (
    <StepGuard basePath={portalRoutes.addDevice.base}>
      <StepFlowLayout
        dataTestid="add-device"
        title={t("heading")}
        step={step}
        totalSteps={totalSteps}
        // The step keys are looked up dynamically, which a literal-typed `t`
        // cannot express - see the flow's constants file.
        stepLabel={t(currentStep.label)}
        description={currentStep.description && t(currentStep.description)}
        menuItems={menuItems}
        menuToggleLabel={tCommon("portal:common.stepMenuLabel")}
        headerAction={
          <div className="flex w-full items-center justify-between">
            <Button
              dataTestid="add-device-cancel"
              variant="tertiary"
              onClick={() => void handleCancel()}
            >
              {tCommon("Common.label.cancel")}
            </Button>
            <AddDeviceOverflowMenu
              basketId={dataContext.basket?.id}
              onBasketDeleted={handleBasketDeleted}
            />
          </div>
        }
      >
        <Outlet context={portalRoutes.addDevice.base} />
      </StepFlowLayout>
    </StepGuard>
  );
};
